import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import type { LabelEntity } from '../../services/types';
import { FormattedDateTime } from '../common/DateTimeWithUTCInfo';
import { DIALOG_STYLES, CONTAINER_STYLES, SECTION_HEADER_STYLES, COLORS, TYPOGRAPHY } from '../policies/constants';

const useStyles = makeStyles({
  body: {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    rowGap: '12px',
    columnGap: '16px',
    alignItems: 'baseline',
  },
  label: { color: tokens.colorNeutralForeground3, fontWeight: 600, fontSize: '13px' },
  value: { wordBreak: 'break-word', fontSize: '14px' },
});

type Props = {
  open: boolean;
  onClose: () => void;
  label: LabelEntity | null;
  baseTenantName?: string;
  targetTenantName?: string;
  /** When provided (View 1 / single-tenant), the dialog reads that tenant's deployed names from inventory. Omitted in the dashboard (cross-tenant) → base names. */
  tenantId?: string;
  /** Configured prefix for the tenant (from TenantConfig). Used only for the not-deployed info note. */
  prefix?: string;
};

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  const s = useStyles();
  return (
    <>
      <Text className={s.label}>{label}</Text>
      <Text className={s.value}>{value ?? <em style={{ color: COLORS.gray600 }}>—</em>}</Text>
    </>
  );
}

export default function LabelViewDetailsDialog({ open, onClose, label, baseTenantName, targetTenantName, tenantId, prefix }: Props) {
  const s = useStyles();

  if (!label) return null;
  const d = label.data;
  const base = baseTenantName || 'Base Tenant';
  const target = targetTenantName || 'Target Tenant';

  // Single-tenant mode (View 1): locate this tenant's deployment so we can read the
  // authoritative deployed names from inventory. Absent in the dashboard (cross-tenant).
  const tenantDeployment = tenantId
    ? label.tenantDeployments.find(t => t.tenantId.toLowerCase() === tenantId.toLowerCase())
    : undefined;
  const isDeployed = tenantDeployment?.deploymentStatus === 'Deployed';

  // Read-don't-compute: deployed → show the stored (possibly prefixed) per-tenant strings;
  // not deployed → show the base/source strings. Never reconstruct `prefix + name` in the UI.
  const parentValue = isDeployed
    ? (tenantDeployment?.deployedParentLabelDisplayName ?? d.parentLabelDisplayName)
    : d.parentLabelDisplayName;
  const displayValue = isDeployed
    ? (tenantDeployment?.deployedDisplayName ?? d.displayName)
    : d.displayName;

  // Only in single-tenant mode, for a not-yet-deployed label with a configured prefix,
  // explain that parent and standalone labels will be prefixed on deploy.
  const showPrefixNote = !!tenantId && !isDeployed && !!prefix;
  // For an already-deployed label in single-tenant mode, confirm it was prefixed for this tenant.
  const showDeployedPrefixNote = !!tenantId && isDeployed && !!prefix;

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface style={DIALOG_STYLES.surfaceLarge}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                aria-label="Close"
                onClick={onClose}
              />
            }
          >
            {d.displayName} – Label Properties ({base} → {target})
            {d.disabled && <Badge appearance="tint" color="warning" style={{ marginLeft: 8 }}>Disabled</Badge>}
          </DialogTitle>
          <DialogContent style={{ padding: 0, overflowY: 'auto', maxHeight: '60vh' }}>
            <div style={CONTAINER_STYLES.infoMessage}>
              <Text style={{ ...TYPOGRAPHY.body, color: COLORS.gray600, lineHeight: 1.5 } as React.CSSProperties}>
                Detailed properties and configuration for this sensitivity label from {base}. This is a read-only view.
              </Text>
              {showPrefixNote && (
                <Text
                  style={
                    {
                      ...TYPOGRAPHY.body,
                      color: COLORS.gray600,
                      lineHeight: 1.5,
                      display: 'block',
                      marginTop: 8,
                    } as React.CSSProperties
                  }
                >
                  When deployed, parent and standalone labels will be prefixed
                  with <strong>{prefix}</strong>; child labels are not prefixed.
                </Text>
              )}
              {showDeployedPrefixNote && (
                <Text
                  style={
                    {
                      ...TYPOGRAPHY.body,
                      color: COLORS.gray600,
                      lineHeight: 1.5,
                      display: 'block',
                      marginTop: 8,
                    } as React.CSSProperties
                  }
                >
                  Parent and standalone labels have been prefixed with{' '}
                  <strong>{prefix}</strong> for this tenant; child labels are not prefixed.
                </Text>
              )}
              {!tenantId && (
                <Text
                  style={
                    {
                      ...TYPOGRAPHY.body,
                      color: COLORS.gray600,
                      lineHeight: 1.5,
                      display: 'block',
                      marginTop: 8,
                    } as React.CSSProperties
                  }
                >
                  Display names shown are base values from the source tenant. When deployed,
                  parent and standalone labels are prefixed with each tenant's
                  own configured prefix, so the deployed name varies by tenant; child labels are not
                  prefixed.
                </Text>
              )}
            </div>
            <div style={SECTION_HEADER_STYLES.sticky}>
              Label Properties
            </div>
            <div style={{ padding: '16px 24px' }}>
              <div className={s.body}>
                <Row label="Parent label" value={parentValue} />
                <Row label="Display name" value={displayValue} />
                <Row label="Internal name" value={d.name} />
                <Row label="Entity ID" value={label.entityId} />
                <Row label="Description" value={d.description} />
                <Row label="Priority" value={d.priority} />
                <Row label="Mode" value={d.mode} />
                <Row label="Workload" value={d.workload} />
                <Row label="Content type" value={d.contentType} />
                {d.isLabelGroup && <Row label="Label group" value="Yes" />}
                {d.isParent && <Row label="Has child labels" value="Yes" />}
                <Row
                  label="Created"
                  value={
                    d.metadata.createdDateTime ? (
                      <FormattedDateTime dateTime={d.metadata.createdDateTime} />
                    ) : null
                  }
                />
                <Row
                  label="Last modified"
                  value={
                    d.metadata.lastModifiedDateTime ? (
                      <FormattedDateTime dateTime={d.metadata.lastModifiedDateTime} />
                    ) : null
                  }
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>Close</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
